import { Link } from "@tanstack/react-router";
import { pickLocalized, type Locale } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ThematicPracticesCarousel } from "@/components/ThematicPracticesCarousel";
import { SanityImage } from "@/components/SanityImage";

const SECTION_SURFACES = [
  { bg: "bg-surface-card-green", numColor: "text-secondary-900", numBadge: "text-text-primary" },
  { bg: "bg-surface-card-beige", numColor: "text-primary-600", numBadge: "text-primary-600" },
  { bg: "bg-surface-card-orange", numColor: "text-accent-800", numBadge: "text-text-primary" },
];

// Map sections to their learn more page by English slug.
const SECTION_LEARN_SLUGS: Record<number, string[]> = {
  0: ["what-are-affirmations-and-how-do-they-work"],
  1: ["mood-matched-tone"],
  2: ["themed-practices-for-any-life-challenges", "themed-practices-for-life-challenges"],
};

export function SanitySections({
  sections,
  learnMorePages,
  locale,
}: {
  sections: any[];
  learnMorePages: any[] | undefined;
  locale: Locale;
}) {
  if (!sections?.length) return null;

  return (
    <section className="flex flex-col gap-8 px-4 sm:px-6 md:px-[60px]">
      {sections.map((s: any, i: number) => {
        const surface = SECTION_SURFACES[i % SECTION_SURFACES.length];
        const reverse = i % 2 === 1;
        const num = String(i + 1).padStart(2, "0");
        const sectionLocalized = s.hasLocalizedImage
          ? (s.localizedImage?.[locale] ?? s.localizedImage?.en)
          : null;
        const sectionImg = sectionLocalized ?? s.image;
        const isPeach = surface.bg === "bg-surface-card-orange";
        const candidates = SECTION_LEARN_SLUGS[i] ?? [];
        const learnMore =
          candidates.length && learnMorePages
            ? learnMorePages.find((p: any) => candidates.includes(p.en))
            : null;
        const learnSlug = learnMore ? (learnMore[locale] ?? learnMore.en) : null;

        const imageBlock = isPeach ? (
          <ThematicPracticesCarousel className="h-[300px] md:h-[360px] lg:aspect-square lg:h-auto" />
        ) : sectionImg ? (
          <SanityImage
            image={sectionImg}
            width={800}
            height={800}
            sizes="(min-width: 1024px) 480px, 100vw"
            alt={pickLocalized(sectionImg?.alt, locale) || pickLocalized(s.title, locale)}
            className="aspect-square w-full rounded-[24px] object-cover"
            loading="lazy"
          />
        ) : null;

        return (
          <div
            key={s._key ?? i}
            className={`grid items-center gap-6 overflow-hidden rounded-[24px] p-6 md:p-10 lg:grid-cols-2 lg:gap-[50px] lg:p-[74px] ${surface.bg}`}
          >
            {reverse && imageBlock && (
              <div className="order-2 flex items-center justify-center lg:order-1">
                {imageBlock}
              </div>
            )}
            <div className={`flex flex-col gap-6 order-1 ${reverse ? "lg:order-2" : ""}`}>
              <div className="flex items-end gap-3">
                <span
                  className={`font-display text-[36px] font-semibold leading-[1] tracking-[0.108px] sm:text-[44px] lg:text-[54px] ${surface.numColor}`}
                >
                  {num}
                </span>
                <span
                  className={`max-w-[280px] text-[14px] font-normal uppercase tracking-[0.2em] ${surface.numBadge}`}
                >
                  {pickLocalized(s.eyebrow, locale)}
                </span>
              </div>
              <h2 className="font-display text-[26px] font-semibold leading-[1.1] text-text-primary sm:text-[34px] lg:text-[46px] lg:leading-[1.05]">
                {pickLocalized(s.title, locale)}
              </h2>
              {s.description && (
                <p className="max-w-[479px] text-[16px] leading-[24px] text-text-primary">
                  {pickLocalized(s.description, locale)}
                </p>
              )}
              {s.cta?.label && (
                <Button
                  asChild
                  variant="mainSecondary"
                  size="main"
                  className="self-start"
                  // Inline style, not a class: tailwind-merge drops the variant's
                  // custom text-b1 class when text-* utilities conflict. text-primary
                  // (vs the variant's text-secondary) clears WCAG AA on all three
                  // section surfaces, orange included.
                  style={{ color: "var(--text-primary)" }}
                >
                  {learnSlug ? (
                    <Link
                      to="/learn/$slug"
                      params={{ slug: learnSlug }}
                      search={locale === "en" ? {} : { lang: "fr" }}
                    >
                      {pickLocalized(s.cta.label, locale)}
                    </Link>
                  ) : (
                    <a href={s.cta.url || "#"}>{pickLocalized(s.cta.label, locale)}</a>
                  )}
                </Button>
              )}
            </div>
            {!reverse && imageBlock && (
              <div className="order-2 flex items-center justify-center">{imageBlock}</div>
            )}
            {!sectionImg && !isPeach && (
              <div
                className={`${reverse ? "order-2 lg:order-1" : ""} aspect-square w-full rounded-[24px] bg-[var(--background-main)]/50`}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </section>
  );
}
